/**
 * Gedeelde types voor de formuliercomponenten in BaseComponents/Form.
 *
 * Deze library verving de monolithische VInput. Ze rendert bewust dezelfde DOM als
 * VInput deed: de styling in scaffold.scss en
 * _v-input.scss hangt aan de structuur (`.v-input > label`, `.v-input div`,
 * `span.suffix`, …), dus elke afwijking in markup is een visuele regressie.
 */

/** Eén kiesbare waarde in een select of radiogroep. */
export interface SelectValue {
    key: string | number | boolean | null
    name: string
    description?: string
    /**
     * Kortere weergave in het gesloten doorzoekbare veld, bv. "BE" voor de optie
     * "BE – België" in het smalle btw-landcodeveld (GH #7020). De lijst toont `name`.
     */
    shortName?: string
    // Optie blijft zichtbaar maar is niet kiesbaar. Nodig voor afgeleide waarden die
    // wel de huidige waarde van het veld kunnen zijn (anders rendert de select leeg). #6399
    disabled?: boolean
}

/** Een `<optgroup>`: `key` is het groepslabel. */
export interface SelectOptGroup {
    key: string
    values: SelectValue[]
}

export type SelectOption = SelectValue | SelectOptGroup

export function isOptGroup(option: SelectOption): option is SelectOptGroup {
    return Array.isArray((option as SelectOptGroup).values)
}

/** Bestand in een dropzone: nieuwe upload of reeds opgeslagen bijlage. */
export interface UploadedFile {
    file?: File
    tempUrl: string
    name?: string
    // Reeds opgeslagen bijlage (komt uit de database, is geen nieuwe upload).
    database_saved?: boolean
}

/** Hoe VDisplayValue een waarde toont in leesmodus. */
export type DisplayType =
    | 'text'
    | 'number'
    | 'eu-number'
    | 'price'
    | 'percentage'
    | 'surface'
    | 'time'

/**
 * Leesmodus voor velden met een waarde (VInput: `display-only-value`). Niet `null` →
 * het veld rendert VDisplayValue i.p.v. het control, met ALLE attributen en listeners
 * op de leesweergave (bv. `@click` om een modal te openen, zoals SurfaceLine doet).
 */
export interface DisplayableProps {
    displayAs?: DisplayType | null
}

export type FieldLayout = 'horizontal' | 'stacked'

/** Types die VTextInput als native `<input type>` rendert. */
export type TextInputType =
    | 'text'
    | 'email'
    | 'password'
    | 'number'
    | 'tel'
    | 'url'
    | 'search'
    | 'time'
    | 'datetime-local'
    | 'month'
    | 'week'

/**
 * Foutmelding(en) die een scherm expliciet meegeeft, bv. `form.errors.name`.
 *
 * `undefined` betekent "niet meegegeven": dan vallen we terug op `page.props.errors`,
 * net als VInput. Zo werken Inertia-formulieren zonder extra code, en kunnen
 * axios-/lokale validaties hun fouten toch tonen. In VInput kon dat niet: 29 schermen
 * gaven `:error` mee, maar dat was geen prop en belandde als HTML-attribuut op de input.
 */
export type FieldError = string | string[] | null | undefined

/**
 * Props die elk veld deelt en doorgeeft aan VFormField.
 *
 * `required` vervangt VInput's `validation: { required }`, `invalid` vervangt
 * `transformOnError`.
 */
export interface FormFieldBaseProps {
    label?: string | null
    name?: string | null
    /** Id van het control-element; standaard gelijk aan `name`. */
    id?: string | null
    required?: boolean
    disabled?: boolean
    example?: string | null
    /**
     * SECURITY: wordt met v-html gerenderd voor opmaak in vertalingen. Geef NOOIT
     * user-controllable data mee zonder escapeHtml().
     */
    info?: string | null
    error?: FieldError
    showErrors?: boolean
    /** Rode rand + achtergrond op het control (VInput: `transformOnError`). */
    invalid?: boolean
    /**
     * - `horizontal` (standaard): label links (1/3, rechts uitgelijnd vanaf lg), veld
     *   rechts (2/3). Onder `sm` staat het label boven het veld.
     * - `stacked`: label altijd boven het veld; ook voor velden zonder label in een
     *   tabelcel (VInput: `noGrid`).
     */
    layout?: FieldLayout
    /**
     * Zonder label tóch in de veldkolom staan (labelkolom vrij), bv. "Adresregel 2" onder
     * het adres. Zonder `indent` vult een veld zonder label de volle breedte (bedoeld voor
     * documentregels); vroeger deed `input-classes="lg:!col-start-2"` dit.
     */
    indent?: boolean
    hasPrefixSelect?: boolean
    labelClasses?: string
    inputClasses?: string
    exampleClasses?: string
    errorClasses?: string
    infoClasses?: string
    suffixClasses?: string
}
